import React, { useState } from 'react';
import { ActionIcon, Badge, Box, Button, Group, Stepper, Text, Tooltip } from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconCalculator, IconDeviceFloppy, IconFileTypePdf, IconX } from '@tabler/icons-react';
import type { ICotizacionServicios } from '../../../modelos/CotizadorServicios/entidades/ICotizacionServicios';
import type { IEquipoServicioAscensor } from '../../../modelos/CotizadorServicios/entidades/IEquipoServicioAscensor';
import type { IEquipoServicioEscalera } from '../../../modelos/CotizadorServicios/entidades/IEquipoServicioEscalera';
import type { IOpcionSelectServicio } from '../../../modelos/CotizadorServicios/entidades/IOpcionesCotizacionServicios';
import type { ITablaEquiposMeta } from '../interfaces';
import { PasoDatosGeneralesVista } from './stepper/PasoDatosGeneralesVista';
import { PasoEquiposVista } from './stepper/PasoEquiposVista';
import { PasoResumenVista } from './stepper/PasoResumenVista';

interface CrearCotizacionServiciosVistaProps {
    cotizacion: ICotizacionServicios;
    paises: IOpcionSelectServicio[];
    nombrePais?: string;
    porcentajeIva: number;
    errores: Record<string, string>;
    soloLectura: boolean;
    hayFilaEnEdicion: boolean;
    requiereRecalculo: boolean;
    alCambiar: <K extends keyof ICotizacionServicios>(campo: K, valor: ICotizacionServicios[K]) => void;
    metaAscensores: ITablaEquiposMeta<IEquipoServicioAscensor>;
    metaEscaleras: ITablaEquiposMeta<IEquipoServicioEscalera>;
    alAgregarAscensor: () => void;
    alAgregarEscalera: () => void;
    alCancelarEdicion: () => void;
    /** Valida el paso y marca los errores; true si se puede avanzar. */
    validarDatosGenerales: () => boolean;
    validarEquipos: () => boolean;
    alCalcular: () => void;
    alGenerarPdf: () => void;
    alGuardar: () => void;
    alVolver: () => void;
}

const ULTIMO_PASO = 2;

/** Encabezado, pasos y navegación de la cotización. El contenido de cada paso es su propia vista. */
export const CrearCotizacionServiciosVista: React.FC<CrearCotizacionServiciosVistaProps> = (props) => {
    const { cotizacion, soloLectura, hayFilaEnEdicion, requiereRecalculo } = props;
    const [paso, setPaso] = useState(0);

    const puedeSalirDelPaso = (actual: number) =>
        actual === 0 ? props.validarDatosGenerales() : actual === 1 ? props.validarEquipos() : true;

    const irAPaso = (destino: number) => {
        // Hacia atrás siempre se puede; hacia adelante se validan los pasos intermedios.
        for (let p = paso; p < destino; p++) if (!puedeSalirDelPaso(p)) return;
        setPaso(destino);
    };

    const sinResultado = !cotizacion.Resultado || requiereRecalculo;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px' }}>
            <Group justify="space-between" mb="md">
                <div>
                    <Group gap="xs">
                        <Text fw={700} size="lg">{cotizacion.Id ? cotizacion.Proyecto || 'Cotización de Servicios' : 'Nueva Cotización de Servicios'}</Text>
                        <Badge variant="light" size="sm" radius="xl" style={{ textTransform: 'none' }}>{cotizacion.Estado?.Nombre ?? 'Borrador'}</Badge>
                    </Group>
                    <Text c="dimmed" size="xs">Mantenimiento preventivo de ascensores, escaleras, rampas y andenes</Text>
                </div>
                <Tooltip label="Volver al listado" withinPortal style={{ fontSize: '10px', padding: '3px 5px' }}>
                    <ActionIcon variant="subtle" color="gray" radius="xl" onClick={props.alVolver}>
                        <IconX size={18} />
                    </ActionIcon>
                </Tooltip>
            </Group>

            <Stepper active={paso} onStepClick={hayFilaEnEdicion ? undefined : irAPaso} size="sm" mb="xl">
                <Stepper.Step label="Datos Generales" description="Proyecto y ajustes comerciales" />
                <Stepper.Step label="Equipos" description="Ascensores y escaleras" />
                <Stepper.Step label="Resumen" description="Cotización y desglose" />
            </Stepper>

            <Box style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                {paso === 0 && (
                    <PasoDatosGeneralesVista cotizacion={cotizacion} paises={props.paises} errores={props.errores} soloLectura={soloLectura} alCambiar={props.alCambiar} />
                )}
                {paso === 1 && (
                    <PasoEquiposVista
                        ascensores={cotizacion.Ascensores}
                        escaleras={cotizacion.Escaleras}
                        metaAscensores={props.metaAscensores}
                        metaEscaleras={props.metaEscaleras}
                        alAgregarAscensor={props.alAgregarAscensor}
                        alAgregarEscalera={props.alAgregarEscalera}
                        alCambiarPestana={props.alCancelarEdicion}
                    />
                )}
                {paso === 2 && (
                    <PasoResumenVista cotizacion={cotizacion} nombrePais={props.nombrePais} porcentajeIva={props.porcentajeIva} requiereRecalculo={requiereRecalculo} />
                )}
            </Box>

            <Group justify="space-between" mt="md">
                <Button variant="light" color="primary" size="xs" radius="lg" leftSection={<IconArrowLeft size={16} />} onClick={() => setPaso(p => p - 1)} disabled={paso === 0 || hayFilaEnEdicion}>
                    Atrás
                </Button>
                {paso < ULTIMO_PASO ? (
                    <Button size="xs" radius="xl" rightSection={<IconArrowRight size={16} />} onClick={() => irAPaso(paso + 1)} disabled={hayFilaEnEdicion}>
                        Siguiente
                    </Button>
                ) : (
                    <Group gap="xs">
                        {!soloLectura && (
                            <Button variant="light" size="xs" radius="xl" leftSection={<IconCalculator size={16} />} onClick={props.alCalcular}>
                                {cotizacion.Resultado ? 'Recalcular' : 'Calcular Cotización'}
                            </Button>
                        )}
                        <Tooltip label="Calcula la cotización para generar el PDF" disabled={!sinResultado} withinPortal style={{ fontSize: '10px', padding: '3px 5px' }}>
                            <Button variant="light" size="xs" radius="xl" leftSection={<IconFileTypePdf size={16} />} onClick={props.alGenerarPdf} disabled={sinResultado}>
                                Generar PDF
                            </Button>
                        </Tooltip>
                        {!soloLectura && (
                            <Button size="xs" radius="xl" leftSection={<IconDeviceFloppy size={16} />} onClick={props.alGuardar}>
                                Guardar
                            </Button>
                        )}
                    </Group>
                )}
            </Group>
        </div>
    );
};
